// src/lib/controle-api.js — CONTRÔLE DES MÉNAGES (UI mobile, client)
//
// Client des 3 actions serveur du contrôle (lib/controles-handlers.js, routées
// par /api/beds24-mapping en POST { action, ... }) :
//   · controle_a_controler   → { menages: [...] } (liste du contrôleur)
//   · controle_run_checklist → checklist LECTURE SEULE d'un ménage fini
//   · controle_create        → dépose le verdict ('bon' | 'a_reprendre')
//
// Le PÉRIMÈTRE est résolu CÔTÉ SERVEUR (controle_log_ids frais / parc
// orchestrateur) — ce client n'envoie jamais de scope, il ne peut donc rien
// élargir. Auth : Bearer JWT Supabase (même pattern que menage-avis-api.js).
//
// Les erreurs HTTP portent leur statut sur `err.status` (le composant en a
// besoin : 403 = pas contrôleur → section masquée ; 409 = déjà contrôlé ;
// 410 = fenêtre 72 h expirée). Une erreur réseau porte `err.reseau = true`
// et un message générique sans PII.

import { supabase } from '../supabase'

const ENDPOINT = '/api/beds24-mapping'

async function postAction(body) {
  let res
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    })
  } catch (e) {
    // Erreur réseau — message générique, jamais de PII.
    const err = new Error('Connexion impossible. Réessayez.')
    err.reseau = true
    throw err
  }
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(json.error || `Erreur ${res.status}`)
    err.status = res.status
    throw err
  }
  return json
}

// La liste des ménages finis (< 72 h) du périmètre du contrôleur.
// 200 avec { menages } (même vide) = le compte est contrôleur ;
// 403 = pas contrôleur (err.status === 403).
export async function fetchAControler({ date } = {}) {
  // `date` (YYYY-MM-DD, passée) → CONSULTATION du jour (navigation par jour,
  // Dimitri 2026-08-21) : menages = [], tout le jour dans deja_controles.
  return postAction({ action: 'controle_a_controler', ...(date ? { date } : {}) })
}

// SONDE d'entrée : le client ne connaît pas le périmètre à l'avance — seul le
// serveur sait si ce compte est contrôleur. true = l'entrée « Contrôle »
// existe ; false = elle n'apparaît pas (403 attendu pour un agent ordinaire,
// silencieux ; toute autre erreur est loggée et masque aussi l'entrée —
// fail-closed, l'onglet réapparaîtra à la prochaine session).
export async function sonderControleur() {
  try {
    await fetchAControler()
    return true
  } catch (e) {
    if (e.status === 403) return false
    console.error('[controle] sonde contrôleur', e?.message || e)
    return false
  }
}

// La checklist EN LECTURE SEULE du ménage contrôlé :
// { agent_prenom, statut, scheduled_date, logement_id, items_checked, coches,
//   photos: [{ url }] } — URLs signées TTL 600 s.
export async function fetchRunChecklist(runId) {
  return postAction({ action: 'controle_run_checklist', run_id: runId })
}

// Dépose le contrôle. verdict 'bon' (rien à signaler) ou 'a_reprendre' avec
// points [{ categorie, texte, photos?: [path…] }] — les paths viennent
// d'uploaderPhotoControle, jamais d'ailleurs (le serveur refuse un path forgé).
// Erreurs : 409 déjà contrôlé · 410 fenêtre expirée · 404 hors périmètre
// (indistinct, voulu côté serveur).
// `reprisePar` (couche « qui reprend », GO Dimitri 2026-08-18) : 'controleur'
// (repris sur place) ou 'agent' — requis serveur pour un verdict 'a_reprendre'.
export async function envoyerControle({ runId, verdict, points = [], reprisePar }) {
  const res = await postAction({
    action: 'controle_create', run_id: runId, verdict, points,
    ...(reprisePar ? { reprise_par: reprisePar } : {}),
  })
  // LA FRISE SE RAFRAÎCHIT TOUTE SEULE (correction Dimitri 22/08) : le dépôt
  // réussi se signale — le Planning ré-charge ses contrôles sans rechargement
  // de page (même patron que fbz:stock-signalements).
  try {
    window.dispatchEvent(new CustomEvent('fbz:controle-depose', { detail: { runId } }))
  } catch { /* environnement sans window (tests node) — silencieux */ }
  return res
}

// « Mes contrôles » (GO Dimitri 2026-08-19) — le SUIVI du contrôleur au-delà
// des 72 h de la liste : ses contrôles déposés (≤ 30 jours, serveur).
export async function fetchMesControles() {
  return postAction({ action: 'controle_mes_controles' })
}

// Plafond de photos par point — MIROIR du serveur (MAX_PHOTOS_PAR_POINT,
// lib/controles-handlers.js) : l'UI bloque au même seuil que le 400 serveur.
export const MAX_PHOTOS_PAR_POINT = 5

// Upload d'UNE photo de constat : la photo transite en base64 par le serveur
// (action controle_photo_upload, service_role) — le contrôleur n'écrit jamais
// en direct dans Storage (les policies RLS menage-photos couvrent le périmètre
// MÉNAGE, pas le périmètre CONTRÔLE). Rend { path } : le path stocké, à
// embarquer dans points[].photos de envoyerControle. Erreurs : 400 non-image /
// trop lourde (3 Mo) · 404 hors périmètre · 409 non fini · 410 fenêtre passée.
export async function uploaderPhotoControle({ runId, mime, dataBase64 }) {
  return postAction({
    action: 'controle_photo_upload',
    run_id: runId,
    mime,
    data_base64: dataBase64,
  })
}

// File/Blob → base64 nu (sans le préfixe data:…;base64,). FileReader natif —
// pas de dépendance ; rejette en clair si le fichier ne se lit pas.
export function fichierEnBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Photo illisible. Réessayez.'))
    reader.onload = () => {
      const s = String(reader.result || '')
      const i = s.indexOf('base64,')
      if (i === -1) return reject(new Error('Photo illisible. Réessayez.'))
      resolve(s.slice(i + 'base64,'.length))
    }
    reader.readAsDataURL(file)
  })
}
